import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthShell } from "../components/auth-shell";
import { useAuthCtx } from "../lib/auth-provider";
import { updateUserProfile } from "../lib/callables";
import { decideAndRedirect } from "../lib/post-auth";
import { readContactProfile, useProfileStatus } from "../lib/profile";

/**
 * Right after signup (and on the next login of an account from before this
 * screen existed): the company and phone, which travel in the license request
 * email so the admin knows who to call. On save it resumes wherever the user
 * was headed — /home, or the SSO hop back to an app — via decideAndRedirect.
 */
export function CompleteProfilePage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuthCtx();
  const { t } = useTranslation();

  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // The layout holds anyone without company and phone on this page, reading
  // the same live listener. Moving on before it has seen the save would get
  // the user bounced straight back here, so leaving waits for it.
  const status = useProfileStatus(user?.uid ?? null);

  function goOn() {
    if (!user) return;
    decideAndRedirect({
      user,
      params,
      nav,
      skipProfileCheck: true,
      onSsoError: () => {
        setErrorKey("auth.errors.ssoMintFailed");
        setBusy(false);
      },
    });
  }

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const q = params.toString();
      nav(`/login${q ? "?" + q : ""}`, { replace: true });
      return;
    }
    let cancelled = false;
    readContactProfile(user.uid)
      .then((p) => {
        if (cancelled) return;
        setCompany(p.company ?? "");
        setPhone(p.phone ?? "");
        setReady(true);
      })
      .catch(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  // Leave once the profile reads complete: right after saving, or on landing
  // here with everything already filled in (a reload, the back button).
  useEffect(() => {
    if (status === "complete" && user && (saved || !busy)) goOn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, saved]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!company.trim() || !phone.trim()) {
      setErrorKey("completeProfile.required");
      return;
    }
    setBusy(true);
    setErrorKey(null);
    try {
      await updateUserProfile({ company: company.trim(), phone: phone.trim() });
      setSaved(true);
    } catch (err) {
      console.error("[complete-profile] save failed", err);
      setErrorKey("completeProfile.saveError");
      setBusy(false);
    }
  }

  if (loading || !user || !ready) return null;

  return (
    <AuthShell formTitle={t("completeProfile.title")} hideLangToggle>
      <form onSubmit={submit} className="acx__form" noValidate>
        <div className="acx__field">
          <label htmlFor="acx-company" className="acx__label">{t("completeProfile.company")}</label>
          <input
            id="acx-company"
            className="acx__input"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            maxLength={120}
            autoComplete="organization"
            autoFocus
            disabled={busy}
            required
          />
        </div>

        <div className="acx__field">
          <label htmlFor="acx-phone" className="acx__label">{t("completeProfile.phone")}</label>
          <input
            id="acx-phone"
            className="acx__input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={40}
            autoComplete="tel"
            placeholder={t("completeProfile.phonePlaceholder")}
            disabled={busy}
            required
          />
        </div>

        {errorKey && (
          <div className="acx__alert acx__alert--danger" role="alert">
            {t(errorKey)}
          </div>
        )}

        <button type="submit" className="acx__btn acx__btn--primary acx__btn--full" disabled={busy}>
          {busy ? t("completeProfile.saving") : t("completeProfile.continue")}
        </button>
      </form>
    </AuthShell>
  );
}
